import React, {
  ChangeEvent,
  DragEventHandler,
  useState,
  useEffect,
} from 'react';
import styled from 'styled-components';
// --------------------------------------------------------------------------------
import { String } from './String';
import { config, Icons } from '../index';
import { TargetProps } from './UserInputs/SelectDropDown';

export interface ImageInputProps {
  /* ImageInput label */
  name?: string;
  /* ImageInput value */
  value?: string;
  /* ImageInput max width */
  maxWidth?: string;
  /* ImageInput min height */
  minHeight?: string;
  /* ImageInput margin */
  margin?: string;
  /* ImageInput padding */
  padding?: string;
  /* ImageInput background color */
  backgroundColor?: string;
  /* ImageInput border radius */
  borderRadius?: string;
  /* Allow multiple file uploads */
  alowMultiple?: boolean;
  /* Color theme */
  themeColor?: string;
  /* ImageInput onChange callBack */
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | TargetProps>
  ) => void;
  /* ImageInput onClear callBack */
  onImgClear?: (event: ChangeEvent<HTMLInputElement>) => void; // handle clear image event
}

export interface InnerProps extends ImageInputProps {
  hasFiles?: any;
}

const StyledTextWrapper = styled.div<InnerProps>`
  position: absolute;
  z-index: 1;
  transform: translate(50%, -50%);
  bottom: 0;
  right: 50%;
  background-color: ${(props) =>
    props.hasFiles ? config.catskillWhite : 'transparent'};
  width: 90%;
  border-radius: ${config.spacingXSmall};
  opacity: 0.8;
`;

const StyledBtnWrapper = styled.div<InnerProps>`
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  cursor: pointer;
  height: ${config.spacingXMedium};
  width: ${config.spacingXMedium};
  border-radius: 50%;
  background-color: ${config.catskillWhite};
  margin: ${config.spacingSmall};
  padding: 2px;
  opacity: 0.6;
  transition: ${config.transitionShort};
  color: ${(props) => props.themeColor};

  &:hover {
    opacity: 1;
  }
`;

const StyledIconWrapper = styled.div<InnerProps>`
  position: absolute;
  z-index: 1;
  top: 50%;
  right: 50%;
  transform: translate(50%, -50%);
`;

const FileUploadWrapper = styled.div<ImageInputProps>`
  position: relative;
  display: flex;
  flex: 1;
  width: 100%;
  height: 100px;
  margin: ${(props) => props.margin};
  user-select: none;
  width: ${(props) => props.maxWidth};
  min-height: ${(props) => props.minHeight};
  border-radius: ${(props) => props.borderRadius};
  overflow: hidden;
  cursor: pointer;
`;

const FileUploadContainer = styled.div<ImageInputProps>`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  margin: auto;
  padding: ${(props) => props.padding};
  background-color: ${(props) => props.backgroundColor};
  transition: ${config.transitionMedium};
`;

// 📌 Primary UI component for user interaction
export const ImageInput: React.FC<ImageInputProps> = ({
  name,
  value,
  // --------------------------------------------------------------------------------
  themeColor = config.cadetBlue,
  maxWidth = '200px',
  minHeight = maxWidth,
  backgroundColor = config.muted,
  borderRadius = config.spacingMedium,
  margin,
  padding,
  alowMultiple = false,
  // --------------------------------------------------------------------------------
  onChange,
  onImgClear,
  // additional props
  ...props
}) => {
  const [hasFiles, setHasFiles] = useState<TargetProps | undefined>();

  // --------------------------------------------------------------------------------
  // 📌  File name display for user interaction
  // --------------------------------------------------------------------------------
  let defaultMsg = 'Drag and drop to upload!';
  const [errorMessage, setErrorMessage] =
    useState<string | undefined>(defaultMsg);

  // --------------------------------------------------------------------------------
  // 📌  Create a reference to the hidden file input element
  // --------------------------------------------------------------------------------
  const hiddenFileInput = React.useRef<HTMLInputElement>(null);

  // --------------------------------------------------------------------------------
  // 📌  Set image if value is set
  // --------------------------------------------------------------------------------
  useEffect(() => {
    if (value) {
      setHasFiles({ url: value });
      setErrorMessage('Existing image');
    }
  }, [value]);

  // Programmatically click the hidden file input element when the Button component is clicked
  const handleInputClick = () => {
    hiddenFileInput?.current?.click();
  };

  const handleFileUploadChange = (files: any) => {
    // if multiple files are allow pass through otherwise just the first file
    const fileContent = alowMultiple ? files : files[0];
    if (!fileContent) return;

    let type = files[0].type;
    let url = URL.createObjectURL(fileContent);
    // accept image files only
    if (!type.includes('image')) {
      setErrorMessage('Only image files are allowed!');
      return;
    }

    setHasFiles({ value: fileContent, name: name || '_', type, url });
    onChange?.({
      target: {
        value: fileContent,
        name: name || '_',
        type,
      },
      isTrusted: true,
    } as any);

    let fileLength = alowMultiple ? files.length : 1;
    // update error messages based in file upload
    if (fileLength === 1)
      setErrorMessage(`${fileContent.name || files[0].name}`);
    // if multiple files are uploaded, display error message
    if (fileLength > 1) setErrorMessage(`${fileLength} file(s) uploaded.`);
  };

  // Call a function (passed as a prop from the parent component) to handle the user-selected file
  const handleFileUpload = (event: ChangeEvent<HTMLInputElement> | any) => {
    const files = event?.target.files;

    // update state with file upload changes
    if (files) handleFileUploadChange(files);
  };

  // TODO event: DragEvent throws TS error
  const drop = (event: DragEventHandler<HTMLDivElement> | any) => {
    event.stopPropagation();
    event.preventDefault();

    const dt = event.dataTransfer;
    const files = dt?.files;

    // update state with file upload changes
    if (files) handleFileUploadChange(files);
  };

  const handleClearSelection = () => {
    // pass removed file as event to handler
    onImgClear?.({
      target: {
        value: undefined,
        name: name || '_', // on clear selection, default to name of input
        type: undefined,
      },
      isTrusted: true,
    } as any);

    setHasFiles(undefined);
    setErrorMessage(defaultMsg);
  };

  return (
    <FileUploadWrapper
      margin={margin}
      maxWidth={maxWidth}
      minHeight={minHeight}
      borderRadius={borderRadius}
    >
      {hasFiles && (
        <StyledBtnWrapper
          themeColor={themeColor}
          onClick={handleClearSelection}
        >
          <StyledIconWrapper>
            <Icons
              icon="close"
              onClick={() => {}} // on hover animation
            />
          </StyledIconWrapper>
        </StyledBtnWrapper>
      )}

      {!hasFiles && (
        <StyledIconWrapper>
          <Icons icon="select-camera" size={120} onClick={handleInputClick} />
        </StyledIconWrapper>
      )}

      <FileUploadContainer
        backgroundColor={backgroundColor}
        padding={padding}
        themeColor={themeColor}
        // --------------------------------------------------------------------------------
        // file upload handlebars to the drop pane for the file upload
        draggable="true"
        onDragOver={(event) => event.preventDefault()}
        onDrop={drop}
        // --------------------------------------------------------------------------------
        onClick={handleInputClick}
        {...props}
      >
        {hasFiles?.url && (
          <img
            src={hasFiles?.url}
            alt={hasFiles?.name}
            style={{ height: '100%', width: '100%', objectFit: 'cover' }}
            loading="lazy"
          />
        )}

        <StyledTextWrapper hasFiles={hasFiles} maxWidth={maxWidth}>
          <String
            color={hasFiles ? themeColor : config.darkMuted}
            fontSize={hasFiles ? config.fontXXSmall : config.fontSmall}
            margin={
              hasFiles
                ? `${config.spacingXSmall} ${config.spacingXMedium}`
                : `${config.spacingXSmall} 0`
            }
            fontWeight="400"
            opacity={hasFiles ? 1 : 0.4}
            limit={1}
            cursor="pointer"
            align="center"
            // --------------------------------------------------------------------------------
            string={errorMessage}
          />
        </StyledTextWrapper>

        <input
          type="file"
          accept="image/png, image/gif, image/jpeg" // accept image files only
          ref={hiddenFileInput}
          onChange={handleFileUpload}
          style={{ display: 'none' }}
          multiple={alowMultiple}
        />
      </FileUploadContainer>
    </FileUploadWrapper>
  );
};
